import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { AdminPartnerDetail, CommissionTermsInput, CommissionModel, CommissionBasis, PayoutFrequency, CommissionStatement, ApiError } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { StatementStatusBadge, formatPeriodLabel } from '@/components/StatementStatusBadge';
import { StatementActions } from '@/pages/admin/Commissions';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/utils';
import { ArrowLeft, ExternalLink, Loader2, RefreshCw } from 'lucide-react';

const SF_BASE = 'https://abby.lightning.force.com/lightning/r/Account';

// Local calendar date (the admin's day, not UTC)
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function emptyTerms(): CommissionTermsInput {
  return {
    model: 'none',
    basis: 'base',
    amount: null,
    percent: null,
    duration_months: null,
    payout_frequency: 'monthly',
    payout_method: 'check',
    minimum_payout: 0,
    effective_from: today(),
    notes: '',
  };
}

export function AdminPartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AdminPartnerDetail | null>(null);
  const [form, setForm] = useState<CommissionTermsInput>(emptyTerms());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!id) return;
    try {
      const data = await api.getPartner(id);
      setDetail(data);
      if (data.terms) {
        setForm({
          model: data.terms.model,
          basis: data.terms.basis,
          amount: data.terms.amount,
          percent: data.terms.percent,
          duration_months: data.terms.duration_months,
          payout_frequency: data.terms.payout_frequency,
          payout_method: 'check',
          minimum_payout: data.terms.minimum_payout,
          effective_from: data.terms.effective_from,
          notes: data.terms.notes || '',
        });
      }
    } catch (err) {
      setError((err as ApiError).error || 'Partner not found');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveTerms = async () => {
    if (!id) return;
    setIsSaving(true);
    setError(null);
    setMessage(null);
    try {
      const { recompute, recompute_error } = await api.updatePartnerTerms(id, {
        ...form,
        amount: form.model === 'per_account' ? Number(form.amount || 0) : null,
        percent: form.model === 'percent_recurring' ? Number(form.percent || 0) : null,
        duration_months: form.model === 'percent_recurring' && form.duration_months ? Number(form.duration_months) : null,
        minimum_payout: Number(form.minimum_payout || 0),
        notes: form.notes || null,
      });
      if (recompute) {
        setMessage(`Terms saved. Rebuilt ${recompute.events} commission line${recompute.events === 1 ? '' : 's'} across ${recompute.periods} open month${recompute.periods === 1 ? '' : 's'}.`);
      } else {
        setMessage('Terms saved. The ledger rebuild could not run right now and will retry automatically.');
        if (recompute_error) console.warn('Recompute error:', recompute_error);
      }
      await load();
    } catch (err) {
      setError((err as ApiError).error || 'Failed to save terms');
    } finally {
      setIsSaving(false);
    }
  };

  const recompute = async () => {
    if (!id) return;
    setIsRecomputing(true);
    setMessage(null);
    try {
      const result = await api.recomputePartnerCommissions(id);
      setMessage(`Rebuilt ${result.events} commission lines across ${result.periods} open months; ${result.statements} pending statement${result.statements === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      setError((err as ApiError).error || 'Recompute failed');
    } finally {
      setIsRecomputing(false);
    }
  };

  const onStatementChanged = (updated: CommissionStatement) => {
    setDetail((d) => (d ? { ...d, statements: d.statements.map((s) => (s.id === updated.id ? { ...s, ...updated, events: undefined } : s)) } : d));
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
        </div>
      </Layout>
    );
  }

  if (!detail) {
    return (
      <Layout>
        <Card>
          <CardContent className="py-8 text-center text-gray-600">{error || 'Partner not found'}</CardContent>
        </Card>
      </Layout>
    );
  }

  const { partner, users, terms, statements } = detail;
  const isAffiliate = partner.partner_type === 'affiliate';

  return (
    <Layout>
      <div className="space-y-6">
        <Link to="/admin/partners" className="inline-flex items-center text-sm text-abby-primary hover:underline">
          <ArrowLeft className="h-4 w-4 mr-1" />
          All partners
        </Link>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{partner.company_name || 'Unnamed partner'}</h1>
            <p className="text-gray-600 mt-1">
              {isAffiliate ? 'Affiliate' : 'Reseller'}
              {partner.referral_code ? <> · code <span className="font-mono">{partner.referral_code}</span></> : null}
            </p>
          </div>
          <div className="flex gap-2">
            {partner.sf_account_id && (
              <a href={`${SF_BASE}/${partner.sf_account_id}/view`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open in Salesforce
                </Button>
              </a>
            )}
          </div>
        </div>

        {message && <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{message}</div>}
        {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>From Salesforce</CardTitle>
              <CardDescription>Read only. Cached on each sync{partner.profile_synced_at ? `, last ${formatDate(partner.profile_synced_at)}` : ''}.</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
                <dt className="text-gray-500">Account ID</dt>
                <dd className="font-mono text-xs break-all">{partner.sf_account_id || '-'}</dd>
                {isAffiliate && (
                  <>
                    <dt className="text-gray-500">Partnership status</dt>
                    <dd>
                      <Badge variant={partner.partnership_status === 'Current' ? 'active' : 'pending'}>
                        {partner.partnership_status || 'Not synced yet'}
                      </Badge>
                    </dd>
                    <dt className="text-gray-500">Referral link</dt>
                    <dd className="break-all">{partner.referral_link || '-'}</dd>
                    <dt className="text-gray-500">Client offer</dt>
                    <dd>{partner.client_offer_text || <span className="text-gray-400">none on the promo template</span>}</dd>
                    <dt className="text-gray-500">Leads</dt>
                    <dd className="tabular-nums">{partner.lead_count}</dd>
                  </>
                )}
                <dt className="text-gray-500">{isAffiliate ? 'Referred accounts' : 'Sub-accounts'}</dt>
                <dd className="tabular-nums">
                  {partner.account_count}
                  {isAffiliate ? ` (${partner.paying_count} paying)` : ''}
                </dd>
              </dl>
            </CardContent>
          </Card>

          {isAffiliate ? (
            <Card>
              <CardHeader>
                <CardTitle>Commission terms</CardTitle>
                <CardDescription>Portal owned. Drives the partner's Earnings page and statements. No clawback.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {(
                    [
                      ['none', 'No commission', "The partner's clients get the discount only"],
                      ['per_account', 'Fixed amount per paying client', 'One-time, in the month of the first paid invoice'],
                      ['percent_recurring', 'Percent of recurring charges', 'Monthly, for a set number of months or for life'],
                    ] as [CommissionModel, string, string][]
                  ).map(([value, label, hint]) => (
                    <label
                      key={value}
                      className={`flex gap-3 items-start p-3 border rounded-lg cursor-pointer ${form.model === value ? 'border-abby-primary bg-abby-primary/5' : ''}`}
                    >
                      <input type="radio" name="model" value={value} checked={form.model === value} onChange={() => setForm({ ...form, model: value })} className="mt-1" />
                      <span>
                        <span className="font-medium text-sm">{label}</span>
                        <span className="block text-xs text-gray-500">{hint}</span>
                      </span>
                    </label>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {form.model === 'per_account' && (
                    <div className="space-y-2">
                      <Label htmlFor="t-amount">Amount per client, USD</Label>
                      <Input id="t-amount" type="number" min={0} step="0.01" value={form.amount ?? ''} onChange={(e) => setForm({ ...form, amount: e.target.value === '' ? null : Number(e.target.value) })} />
                    </div>
                  )}
                  {form.model === 'percent_recurring' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="t-percent">Percent</Label>
                        <Input id="t-percent" type="number" min={0} max={100} step="0.01" value={form.percent ?? ''} onChange={(e) => setForm({ ...form, percent: e.target.value === '' ? null : Number(e.target.value) })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="t-basis">Basis</Label>
                        <select id="t-basis" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.basis} onChange={(e) => setForm({ ...form, basis: e.target.value as CommissionBasis })}>
                          <option value="base">Recurring fees only (no overage)</option>
                          <option value="total">Total revenue billed</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="t-months">Duration in months (blank for life)</Label>
                        <Input id="t-months" type="number" min={1} value={form.duration_months ?? ''} onChange={(e) => setForm({ ...form, duration_months: e.target.value === '' ? null : Number(e.target.value) })} />
                      </div>
                    </>
                  )}
                  {form.model !== 'none' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="t-freq">Payout frequency</Label>
                        <select id="t-freq" className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.payout_frequency} onChange={(e) => setForm({ ...form, payout_frequency: e.target.value as PayoutFrequency })}>
                          <option value="monthly">Monthly</option>
                          <option value="quarterly">Quarterly</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="t-min">Minimum payout, USD</Label>
                        <Input id="t-min" type="number" min={0} step="0.01" value={form.minimum_payout} onChange={(e) => setForm({ ...form, minimum_payout: Number(e.target.value || 0) })} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="t-eff">Effective from</Label>
                        <Input id="t-eff" type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} />
                      </div>
                      <div className="space-y-2 col-span-2">
                        <Label htmlFor="t-notes">Notes</Label>
                        <Input id="t-notes" value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Agreement reference, special conditions" />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <Button onClick={saveTerms} disabled={isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Save terms
                  </Button>
                  <Button variant="outline" onClick={recompute} disabled={isRecomputing || !terms}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isRecomputing ? 'animate-spin' : ''}`} />
                    Rebuild open months
                  </Button>
                  {terms && <span className="text-xs text-gray-500">Payout by check. {terms.description}</span>}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Wholesale billing</CardTitle>
                <CardDescription>Reseller partners are billed wholesale. Manage rates under Billing.</CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/admin/billing">
                  <Button variant="outline">Open Billing</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </div>

        {isAffiliate && (
          <Card>
            <CardHeader>
              <CardTitle>Statements</CardTitle>
              <CardDescription>Approve after review. Mark paid once the check goes out; per-client fees are then flagged as paid out in Salesforce.</CardDescription>
            </CardHeader>
            <CardContent>
              {statements.length === 0 ? (
                <p className="text-sm text-gray-600">No statements yet. They appear once a payout period with commission has ended.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 font-medium">Period</th>
                        <th className="pb-2 font-medium text-right">Lines</th>
                        <th className="pb-2 font-medium text-right">Amount</th>
                        <th className="pb-2 font-medium">Status</th>
                        <th className="pb-2 font-medium"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {statements.map((s) => (
                        <tr key={s.id}>
                          <td className="py-2 font-medium whitespace-nowrap">
                            {formatPeriodLabel(s.period_label)}
                            <span className="block text-xs text-gray-500 font-normal">
                              {formatDateOnly(s.period_start)} to {formatDateOnly(s.period_end)}
                            </span>
                          </td>
                          <td className="py-2 text-right tabular-nums">{s.line_count}</td>
                          <td className="py-2 text-right tabular-nums">
                            {formatCurrency(s.total_amount)}
                            {s.below_minimum && <span className="block text-xs text-amber-700">below minimum</span>}
                          </td>
                          <td className="py-2">
                            <StatementStatusBadge status={s.status} />
                          </td>
                          <td className="py-2 text-right">
                            <StatementActions statement={s} onChanged={onStatementChanged} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>Logins for this partner. Create, reset or impersonate under Users.</CardDescription>
          </CardHeader>
          <CardContent>
            {users.length === 0 ? (
              <p className="text-sm text-gray-600">No users yet.</p>
            ) : (
              <ul className="text-sm divide-y">
                {users.map((u) => (
                  <li key={u.id} className="py-2 flex items-center justify-between gap-3">
                    <span>
                      <span className="text-gray-900">{u.email}</span>
                      <span className="block text-xs text-gray-500">{u.reseller_tier} · created {formatDate(u.created_at)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3">
              <Link to="/admin/users">
                <Button variant="outline" size="sm">
                  Manage users
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
